import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectFiles,updateFileField } from '../../../Store/userSlice';
import DocumentUploadSection from '../../common/FileUpload/DocumentUploadSection';
import { base64ToFile } from '../../common/Util/Util';
import { kycDocUploadValidation } from '../../common/ValidationRules/validationRules';

const KYCDocUploadForm = React.forwardRef((props, ref) => {
  const files = useSelector(selectFiles);
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isIncomeExpanded, setIsIncomeExpanded] = useState(false);
  const [isCoApplicantExpanded, setIsCoApplicantExpanded] = useState(false);
  const [isCoApplicantIncomeExpanded, setIsCoApplicantIncomeExpanded] = useState(false);
  const currentYear = new Date().getFullYear();

  const {
    control,
    formState: { errors },
    trigger,
    setValue
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      applicantPan: null,
      applicantAadhar: null,
      applicantItr2024: null,
      applicantItr2023: null,
      applicantItr2022: null,
      applicantForm16: null,
      coApplicantPan: null,
      coApplicantAadhar: null,
      coApplicantItr2024: null,
      coApplicantItr2023: null,
      coApplicantItr2022: null,
      coApplicantForm16: null
    }
  });

  const convertAndSetfile = (fieldName, fileData) => {
    if (fileData?.base64 && fileData?.filename) {
      const fileObj = base64ToFile(fileData.base64, fileData.filename);
      return fileObj
    } else if (fileData?.remote === true && (fileData?.url || fileData?.documentPath)) {
      return fileData;
    } else {
      return null
    }
  };

  // Helper function to handle ITR file uploads
  const handleItrFileSelect = (field, index) => async (selectedFile) => {
    try {
      if (selectedFile) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const fileData = {
            base64: base64Data,
            filename: selectedFile.name,
          };
          
          // Get current array or initialize empty array
          const currentItrFiles = Array.isArray(files[field]) ? [...files[field]] : [];
          
          // Update or add at specific index
          if (currentItrFiles.length > index) {
            currentItrFiles[index] = fileData;
          } else {
            // Fill any gaps with null values
            while (currentItrFiles.length < index) {
              currentItrFiles.push(null);
            }
            currentItrFiles.push(fileData);
          }
          
          dispatch(updateFileField({
            field: field,
            value: currentItrFiles
          }));
        };
        reader.readAsDataURL(selectedFile);
      }
    } catch (error) {
      console.error('Failed to process file:', error);
    }
  };

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const result = await trigger();
      return result;
    }
  }));

  useEffect(() => {
    setValue('applicantPan', convertAndSetfile("applicantPan", files.applicantPan));
    setValue('applicantAadhar', convertAndSetfile("applicantAadhar", files.applicantAadhar));
    
    // Set ITR values if available
    if (Array.isArray(files.applicantItr) && files.applicantItr.length > 0) {
      setValue('applicantItr2024', convertAndSetfile("applicantItr", files.applicantItr[0]));
      if (files.applicantItr.length > 1) {
        setValue('applicantItr2023', convertAndSetfile("applicantItr", files.applicantItr[1]));
      }
      if (files.applicantItr.length > 2) {
        setValue('applicantItr2022', convertAndSetfile("applicantItr", files.applicantItr[2]));
      }
    }
    
    setValue('applicantForm16', convertAndSetfile("applicantForm16", files.applicantForm16));
    setValue('coApplicantPan', convertAndSetfile("coApplicantPan", files.coApplicantPan));
    setValue('coApplicantAadhar', convertAndSetfile("coApplicantAadhar", files.coApplicantAadhar));
    
    // Set Co-Applicant ITR values if available
    if (Array.isArray(files.coApplicantItr) && files.coApplicantItr.length > 0) {
      setValue('coApplicantItr2024', convertAndSetfile("coApplicantItr", files.coApplicantItr[0]));
      if (files.coApplicantItr.length > 1) {
        setValue('coApplicantItr2023', convertAndSetfile("coApplicantItr", files.coApplicantItr[1]));
      }
      if (files.coApplicantItr.length > 2) {
        setValue('coApplicantItr2022', convertAndSetfile("coApplicantItr", files.coApplicantItr[2]));
      }
    }
    
    setValue('coApplicantForm16', convertAndSetfile("coApplicantForm16", files.coApplicantForm16));
  }, [files, setValue]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleIncomeExpand = () => {
    setIsIncomeExpanded(!isIncomeExpanded);
  };

  const toggleCoApplicantExpand = () => {
    setIsCoApplicantExpanded(!isCoApplicantExpanded);
  };

  const toggleCoApplicantIncomeExpand = () => {
    setIsCoApplicantIncomeExpanded(!isCoApplicantIncomeExpanded);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        Step 2/4
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon sx={{ color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          Upload files in JPEG, PNG, or PDF format, up to 5 MB in size.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box 
            sx={{ 
              p: 3, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
            onClick={toggleExpand}
          >
            <Typography variant="h6">
              Upload Applicant Documents
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <IconButton>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isExpanded}>
            <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', backgroundColor: 'white' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <DocumentUploadSection
                  control={control}
                  title="Applicant PAN (Permanent Account Number)"
                  name="applicantPan"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicant}
                  errors={errors?.applicantPan}
                  helptext={errors?.applicantPan?.message}
                  value={convertAndSetfile("applicantPan", files?.applicantPan)}
                />
                <DocumentUploadSection
                  control={control}
                  title="Applicant Aadhar card"
                  name="applicantAadhar"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicant}
                  errors={errors?.applicantAadhar}
                  helptext={errors?.applicantAadhar?.message}
                  value={convertAndSetfile("applicantAadhar", files?.applicantAadhar)}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box 
            sx={{ 
              p: 3, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
            onClick={toggleIncomeExpand}
          >
            <Typography variant="h6">
              Upload Applicant Income Details Documents
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <IconButton>
              {isIncomeExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isIncomeExpanded}>
            <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', backgroundColor: 'white' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <DocumentUploadSection
                  control={control}
                  title={`Upload Applicant Income Tax Return (FY ${currentYear - 1})`}
                  name="applicantItr2024"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.applicantItr2024}
                  helptext={errors?.applicantItr2024?.message}
                  value={Array.isArray(files?.applicantItr) && files?.applicantItr[0] ? convertAndSetfile("applicantItr", files?.applicantItr[0]) : null}
                  onFileSelect={handleItrFileSelect("applicantItr", 0)}
                />
                <DocumentUploadSection
                  control={control}
                  title={`Upload Applicant Income Tax Return (FY ${currentYear - 2})`}
                  name="applicantItr2023"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.applicantItr2023}
                  helptext={errors?.applicantItr2023?.message}
                  value={Array.isArray(files?.applicantItr) && files?.applicantItr[1] ? convertAndSetfile("applicantItr", files?.applicantItr[1]) : null}
                  onFileSelect={handleItrFileSelect("applicantItr", 1)}
                />
                <DocumentUploadSection
                  control={control}
                  title={`Upload Applicant Income Tax Return (FY ${currentYear - 3})`}
                  name="applicantItr2022"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.applicantItr2022}
                  helptext={errors?.applicantItr2022?.message}
                  value={Array.isArray(files?.applicantItr) && files?.applicantItr[2] ? convertAndSetfile("applicantItr", files?.applicantItr[2]) : null}
                  onFileSelect={handleItrFileSelect("applicantItr", 2)}
                />
                <DocumentUploadSection
                  control={control}
                  title="Applicant Latest Form 16 (If Salaried)"
                  name="applicantForm16"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantForm16}
                  errors={errors?.applicantForm16}
                  helptext={errors?.applicantForm16?.message}
                  value={convertAndSetfile("applicantForm16", files?.applicantForm16)}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box 
            sx={{ 
              p: 3, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
            onClick={toggleCoApplicantExpand}
          >
            <Typography variant="h6">
              Upload Co Applicant Documents
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <IconButton>
              {isCoApplicantExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isCoApplicantExpanded}>
            <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', backgroundColor: 'white' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <DocumentUploadSection
                  control={control}
                  title="Co Applicant PAN (Permanent Account Number)"
                  name="coApplicantPan"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicant}
                  errors={errors?.coApplicantPan}
                  helptext={errors?.coApplicantPan?.message}
                  value={convertAndSetfile("coApplicantPan", files?.coApplicantPan)}
                />
                <DocumentUploadSection
                  control={control}
                  title="Co Applicant Aadhar card"
                  name="coApplicantAadhar"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicant}
                  errors={errors?.coApplicantAadhar}
                  helptext={errors?.coApplicantAadhar?.message}
                  value={convertAndSetfile("coApplicantAadhar", files?.coApplicantAadhar)}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box 
            sx={{ 
              p: 3, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
            onClick={toggleCoApplicantIncomeExpand}
          >
            <Typography variant="h6">
              Upload Co Applicant Income Details Documents
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <IconButton>
              {isCoApplicantIncomeExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isCoApplicantIncomeExpanded}>
            <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', backgroundColor: 'white' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <DocumentUploadSection
                  control={control}
                  title={`Upload Co Applicant Income Tax Return (FY ${currentYear - 1})`}
                  name="coApplicantItr2024"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.coApplicantItr2024}
                  helptext={errors?.coApplicantItr2024?.message}
                  value={Array.isArray(files?.coApplicantItr) && files?.coApplicantItr[0] ? convertAndSetfile("coApplicantItr", files?.coApplicantItr[0]) : null}
                  onFileSelect={handleItrFileSelect("coApplicantItr", 0)}
                />
                <DocumentUploadSection
                  control={control}
                  title={`Upload Co Applicant Income Tax Return (FY ${currentYear - 2})`}
                  name="coApplicantItr2023"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.coApplicantItr2023}
                  helptext={errors?.coApplicantItr2023?.message}
                  value={Array.isArray(files?.coApplicantItr) && files?.coApplicantItr[1] ? convertAndSetfile("coApplicantItr", files?.coApplicantItr[1]) : null}
                  onFileSelect={handleItrFileSelect("coApplicantItr", 1)}
                />
                <DocumentUploadSection
                  control={control}
                  title={`Upload Co Applicant Income Tax Return (FY ${currentYear - 3})`}
                  name="coApplicantItr2022"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantIncomeTax}
                  errors={errors?.coApplicantItr2022}
                  helptext={errors?.coApplicantItr2022?.message}
                  value={Array.isArray(files?.coApplicantItr) && files?.coApplicantItr[2] ? convertAndSetfile("coApplicantItr", files?.coApplicantItr[2]) : null}
                  onFileSelect={handleItrFileSelect("coApplicantItr", 2)}
                />
                <DocumentUploadSection
                  control={control}
                  title="Co Applicant Latest Form 16 (If Salaried)"
                  name="coApplicantForm16"
                  setValue={setValue}
                  validationRule={kycDocUploadValidation?.applicantForm16}
                  errors={errors?.coApplicantForm16}
                  helptext={errors?.coApplicantForm16?.message}
                  value={convertAndSetfile("coApplicantForm16", files?.coApplicantForm16)}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
});

export default KYCDocUploadForm;
